import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Folder, Paperclip, Square } from "lucide-react";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { ConnectorsPopover } from "~/pages/manifold-nextgen/components/conversations/connectors/ConnectorsPopover";
import type { McpGuidelines } from "~/pages/manifold-nextgen/components/conversations/connectors/useMcpGuidelines";
import type { ContextBuffer } from "~/pages/manifold-nextgen/components/conversations/contextBuffer";
import {
  SkillEditorMode,
  type SkillFormData,
  type SkillFormErrors,
} from "~/pages/manifold-nextgen/components/conversations/skills/SkillEditorForm";
import { SkillEditorModal } from "~/pages/manifold-nextgen/components/conversations/skills/SkillEditorModal";
import { SkillsList } from "~/pages/manifold-nextgen/components/conversations/skills/SkillsList";
import { SkillsPopover } from "~/pages/manifold-nextgen/components/conversations/skills/SkillsPopover";
import { useSkillMutations } from "~/pages/manifold-nextgen/components/conversations/skills/useSkillMutations";
import type { AgentConnector } from "~/services/agents";
import { MAX_UPLOAD_BYTES, uploadTooLargeMessage } from "~/services/artifacts";
import { FeaturedSession } from "~/services/projects";
import { PartialSkill } from "~/services/skills";
import { zendeskArticleUrl } from "~/utils/zendesk";
import { AgentSwitcherPopover } from "./AgentSwitcherPopover";
import { AttachmentChip } from "./AttachmentChip";
import { ContextBufferMeter } from "./ContextBufferMeter";
import { FileDropOverlay } from "./FileDropOverlay";
import { Mention, MentionInput, type MentionInputRef } from "./MentionInput";
import { SharePopover } from "./SharePopover";
import { getComposerDraft, setComposerDraft } from "./composerDraft";
import { useAttachmentTray } from "./useAttachmentTray";
import { usePageFileDrop } from "./usePageFileDrop";

const SKILLS_HELP_URL = zendeskArticleUrl("43648526871437-AI-Skills-Guide");

interface ComposerProps {
  // Inert composer — input and submit disabled. Used by the loading skeleton
  // on mount, and while a human-in-the-loop tool awaits the user's answer.
  disabled?: boolean;
  // True while a run is streaming. Swaps the Send button for a Stop button
  // that calls `onCancel` instead of submitting.
  isRunning?: boolean;
  // True between the moment the user clicks Stop and the moment the agent
  // emits RUN_FINISHED. Disables the button to prevent duplicate cancels.
  isCancelling?: boolean;
  onCancel?: () => void;
  // The composer gathers text and holds attachments; the caller owns what
  // happens to them. Rejecting keeps the text and tray so the user can retry.
  onSubmit: (text: string, files: File[]) => void | Promise<void>;
  placeholder?: string;
  // Skills available in this conversation's context. Empty list during
  // loading or when no skills apply is rendered as a friendly empty state.
  skills?: PartialSkill[];
  // Maps a DATASET skill's scope_id → its dataset display name, for the
  // "registered from" info tooltip in the skills picker.
  datasetNames?: Record<string, string>;
  // Connectors granted to this conversation's agent, the names currently
  // enabled, and a toggle callback. Selection is owned and persisted upstream.
  connectors?: AgentConnector[];
  enabledConnectorNames?: string[];
  onToggleConnector?: (connectorName: string) => void;
  // MCP-guidelines acceptance gate for the connectors picker.
  mcpGuidelines?: McpGuidelines;
  // Context-window token usage for the current run, or null before any tokens
  // have been sent — in which case the usage meter is hidden.
  contextBuffer?: ContextBuffer | null;
  starterPromptValue?: string;
  // The id of the conversation this composer belongs to, used to surface the
  // inline Share picker. Absent on the new-session splash (nothing to share yet).
  conversationId?: string;
  // Draft persistence key for callers with no conversationId yet (e.g. the
  // new-session splash, keyed per project). Ignored when conversationId is set.
  draftKey?: string;
  // Names of the dataset(s) backing this conversation's context, shown as a
  // folder indicator in the toolbar. A single entry today; the indicator is
  // hidden when the list is empty.
  contextDatasetNames?: string[];
  // Project this conversation belongs to, and whether the caller owns it —
  // gates the Share popover's "Add to Featured Sessions" toggle.
  projectId?: string;
  isOwner?: boolean;
  // Needed to feature the session from the Share popover: the rail stores a
  // name and agent snapshot, not a reference back to the conversation.
  displayName?: string;
  agentId?: string | null;
  featuredSessions?: FeaturedSession[];
  // The agent handling the next turn, for the composer's agent switcher. Owned
  // above so the same value labels the switcher and attributes the next live
  // turn.
  currentAgentId?: string | null;
  onAgentSwitched?: (agentId: string) => void;
}

export const Composer = ({
  disabled,
  isRunning,
  isCancelling,
  onCancel,
  onSubmit,
  placeholder = "What would you like to know?",
  skills = [],
  datasetNames,
  connectors = [],
  enabledConnectorNames = [],
  onToggleConnector,
  mcpGuidelines,
  contextBuffer,
  starterPromptValue,
  conversationId,
  draftKey,
  contextDatasetNames = [],
  projectId,
  isOwner = false,
  displayName = "",
  agentId,
  featuredSessions,
  currentAgentId,
  onAgentSwitched,
}: ComposerProps) => {
  const inputRef = useRef<MentionInputRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError } = useToast();
  const draftId = conversationId ?? draftKey;
  // Seeded from any saved draft so a session switch doesn't reset it.
  const [text, setText] = useState(() => getComposerDraft(draftId));
  // True while a send's uploads are in flight — blocks re-entry and drives the
  // chips' spinner. A ref backs it because two Enter presses in one tick both
  // read the stale pre-render state, which alone can't stop a double submit.
  const [isSending, setIsSending] = useState(false);
  const sendingRef = useRef(false);

  const {
    items: attachmentItems,
    addFiles,
    removeItem,
    clear: clearAttachments,
    files,
  } = useAttachmentTray();

  // Every attach path routes through here, so an oversized file is refused with a
  // reason when picked. The service enforces the same limit; this only saves the
  // user the round trip.
  const attachFiles = useCallback(
    (incoming: File[]) => {
      const accepted: File[] = [];
      for (const file of incoming) {
        if (file.size > MAX_UPLOAD_BYTES) showError(uploadTooLargeMessage(file.name, file.size));
        else accepted.push(file);
      }
      if (accepted.length > 0) addFiles(accepted);
    },
    [addFiles, showError]
  );

  // Files can arrive three ways — the attach button, a paste, or a drop — and
  // all three are closed off at the same times the attach button is.
  const attachDisabled = Boolean(disabled || isRunning);
  const isDraggingFiles = usePageFileDrop(attachFiles, attachDisabled);

  const handleTextChange = (value: string) => {
    setText(value);
    setComposerDraft(draftId, value);
  };

  useEffect(() => {
    if (starterPromptValue) {
      handleTextChange(starterPromptValue);
    }
  }, [starterPromptValue]);

  const { createUserSkill, updateSkill, deleteSkill, getSkill } = useSkillMutations();

  // Skill editor modal state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<SkillEditorMode>(SkillEditorMode.CREATE);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editingSkillData, setEditingSkillData] = useState<Partial<SkillFormData> | undefined>();
  const [editorErrors, setEditorErrors] = useState<SkillFormErrors>({});
  const [savingSkill, setSavingSkill] = useState(false);
  const [loadingSkillContent, setLoadingSkillContent] = useState(false);

  // Only USER skills expose a delete action, and deleting one is permanent, so
  // we confirm before removing it.
  const [skillPendingDelete, setSkillPendingDelete] = useState<PartialSkill | null>(null);

  // Insert a "/skill" token from the toolbar picker at the end of the input.
  const insertSkillToken = (token: string) => {
    const needsSpace = text.length > 0 && !text.endsWith(" ") && !text.endsWith("\n");
    handleTextChange(`${text}${needsSpace ? " " : ""}${token} `);
    setTimeout(() => inputRef.current?.focusAtEnd(), 0);
  };

  const openSkillsHelp = () => window.open(SKILLS_HELP_URL, "_blank", "noopener,noreferrer");

  const handleAddSkill = () => {
    setEditorMode(SkillEditorMode.CREATE);
    setEditingSkillId(null);
    setEditingSkillData(undefined);
    setEditorErrors({});
    setEditorOpen(true);
  };

  // Opens the editor for an existing skill, fetching its full content. EDIT is
  // used for the caller's own USER skills; VIEW is the read-only mode for
  // AGENT/DATASET skills, which can be inspected but not changed here.
  const openExistingSkill = async (
    skill: PartialSkill,
    mode: typeof SkillEditorMode.EDIT | typeof SkillEditorMode.VIEW
  ) => {
    setEditorMode(mode);
    setEditingSkillId(skill.id);
    setEditingSkillData(undefined);
    setEditorErrors({});
    setEditorOpen(true);
    setLoadingSkillContent(true);
    try {
      const full = await getSkill(skill.id);
      setEditingSkillData({
        name: full.name,
        description: full.description ?? "",
        content: full.content,
      });
    } catch (error) {
      console.error("Failed to load skill:", error);
      setEditorErrors({ general: "Failed to load skill content" });
    } finally {
      setLoadingSkillContent(false);
    }
  };

  const handleEditSkill = (skill: PartialSkill) => openExistingSkill(skill, SkillEditorMode.EDIT);
  const handleViewSkill = (skill: PartialSkill) => openExistingSkill(skill, SkillEditorMode.VIEW);

  // Only USER skills expose a delete action in the picker, and deleting one is
  // permanent — so gate it behind a confirmation dialog.
  const handleDeleteSkill = (skill: PartialSkill) => setSkillPendingDelete(skill);

  const handleConfirmDeleteSkill = async (skill: PartialSkill) => {
    try {
      await deleteSkill(skill);
    } catch (error) {
      console.error("Failed to delete skill:", error);
      showError("Couldn't delete the skill. Try again in a moment.");
      throw error;
    }
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    setSavingSkill(true);
    setEditorErrors({});
    try {
      if (editingSkillId) {
        await updateSkill(editingSkillId, data);
      } else {
        await createUserSkill(data);
      }
      setEditorOpen(false);
      setEditingSkillId(null);
      setEditingSkillData(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save skill";
      setEditorErrors({ general: message });
    } finally {
      setSavingSkill(false);
    }
  };

  const submit = async () => {
    const trimmed = text.trim();
    // A turn must carry text or at least one attachment; a fully empty send is
    // refused here and by the disabled Send button. An attachment-only turn is
    // allowed — the agent hydrates a placeholder so the model never sees an
    // empty text block.
    if ((!trimmed && files.length === 0) || disabled || isRunning || sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    setIsSending(true);
    // Cleared before the send: onSubmit can unmount this composer (the
    // new-session splash navigating away) or mount another for the same
    // conversation (an empty session's first turn), which seeds from the draft.
    setComposerDraft(draftId, "");
    try {
      await onSubmit(trimmed, files);
      setText("");
      clearAttachments();
    } catch (err) {
      // Keep the text and files so the user can retry without redoing their
      // work. The thrown message names what failed; nothing else surfaces it.
      setComposerDraft(draftId, text);
      showError(err instanceof Error ? err.message : "Couldn't send your message.");
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  const handleFilesSelected = (fileList: FileList | null) => {
    if (fileList && fileList.length > 0) {
      attachFiles(Array.from(fileList));
    }
    // Reset so re-selecting the same file fires another change event.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // MentionInput swallows Enter while its suggestion dropdown is open, so this
  // only fires for a plain Enter that should send the message.
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Single label for the folder indicator: the conversation's context
  // dataset(s), comma-joined when there's more than one.
  const datasetLabel = contextDatasetNames.join(", ");

  const hasContent = text.trim().length > 0 || files.length > 0;
  const canSubmit = hasContent && !disabled && !isRunning && !isSending;
  const canCancel = Boolean(isRunning && onCancel && !isCancelling);
  // While running, the action button stops the run; otherwise it sends.
  const buttonActive = isRunning ? canCancel : canSubmit;
  const buttonLabel = isRunning ? (isCancelling ? "Cancelling…" : "Stop generation") : "Send";

  return (
    <>
      {isDraggingFiles && <FileDropOverlay />}
      <div
        data-testid="nextgen-composer-shell"
        className={cn(
          "mx-auto w-full max-w-[60rem] rounded-2xl border border-border",
          disabled ? "cursor-not-allowed bg-muted" : "cursor-text bg-background"
        )}
        onMouseDown={(e) => {
          // Focus the editor when the user clicks anywhere in the shell that
          // isn't a button or the editor itself; preserves native button clicks.
          // Inert while disabled so the whole shell reads as non-interactive.
          if (disabled) return;
          if ((e.target as HTMLElement).closest("button, [contenteditable]")) return;
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="px-4 pt-3">
          <MentionInput
            ref={inputRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            ariaLabel="Message"
            maxHeight={256}
            autoFocus
            disabled={disabled}
            onPasteFiles={attachDisabled ? undefined : attachFiles}
          >
            <Mention
              trigger="/"
              type="skill"
              pattern={/\/[\w-]+/g}
              data={skills}
              // Warm, high-contrast token treatment (dark text on light orange)
              // for the inserted skill — more readable than the low-contrast
              // primary-on-primary default, and stable across light/dark themes.
              // Uses the same monospace font + weight as the skills picker rows.
              className="mx-0.5 inline-block rounded bg-orange-100 px-1.5 py-0 font-mono font-medium text-neutral-950"
              displayTransform={(skill) => (skill as PartialSkill).name}
              renderSuggestions={(props) => (
                <div
                  style={{
                    position: "fixed",
                    top: props.position.top - 8,
                    left: props.position.left,
                    transform: "translateY(-100%)",
                    zIndex: 50,
                  }}
                  className="w-96 overflow-hidden rounded-lg border border-border bg-popover shadow-md"
                >
                  <SkillsList
                    skills={skills}
                    searchFilter={props.searchText}
                    onSelectSkill={(name) => props.onSelect(`/${name}`)}
                    onAddSkill={() => {
                      handleAddSkill();
                      props.onClose();
                    }}
                    onEditSkill={(skill) => {
                      void handleEditSkill(skill);
                      props.onClose();
                    }}
                    onDeleteSkill={handleDeleteSkill}
                    onViewSkill={(skill) => {
                      void handleViewSkill(skill);
                      props.onClose();
                    }}
                    datasetNames={datasetNames}
                    onLearnMore={openSkillsHelp}
                    enableKeyboardNav
                    onClose={props.onClose}
                  />
                </div>
              )}
            />
          </MentionInput>
        </div>

        {attachmentItems.length > 0 && (
          <div className="flex flex-wrap gap-3 px-4 pb-1 pt-2">
            {attachmentItems.map((item) => (
              <AttachmentChip
                key={item.key}
                item={item}
                uploading={isSending}
                onRemove={removeItem}
              />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 px-2 py-2">
          <div className="flex items-center gap-1">
            {conversationId && projectId && (
              <AgentSwitcherPopover
                projectId={projectId}
                currentAgentId={currentAgentId}
                onSwitched={onAgentSwitched}
                disabled={Boolean(disabled || isRunning)}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachDisabled}
                  size="icon"
                  variant="ghost"
                  aria-label="Attach files"
                  className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                >
                  <Paperclip className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Attach files</TooltipContent>
            </Tooltip>
            {onToggleConnector && mcpGuidelines && (
              <ConnectorsPopover
                connectors={connectors}
                enabledNames={enabledConnectorNames}
                onToggle={onToggleConnector}
                mcpGuidelines={mcpGuidelines}
              />
            )}
            <SkillsPopover
              skills={skills}
              onSelect={insertSkillToken}
              onAddSkill={handleAddSkill}
              onEditSkill={(skill) => void handleEditSkill(skill)}
              onDeleteSkill={handleDeleteSkill}
              onViewSkill={(skill) => void handleViewSkill(skill)}
              datasetNames={datasetNames}
              onLearnMore={openSkillsHelp}
            />
            {conversationId && projectId && (
              <SharePopover
                conversationId={conversationId}
                projectId={projectId}
                isOwner={isOwner}
                displayName={displayName}
                agentId={agentId}
                featuredSessions={featuredSessions}
              />
            )}
            {datasetLabel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default items-center px-1.5 text-muted-foreground">
                    <Folder className="size-4" aria-label={datasetLabel} />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{datasetLabel}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-3">
            {contextBuffer && <ContextBufferMeter buffer={contextBuffer} />}
            <Button
              type="button"
              onClick={isRunning ? onCancel : submit}
              disabled={!buttonActive}
              size="icon"
              variant="ghost"
              aria-label={buttonLabel}
              className={cn(
                "group size-8 rounded-full",
                buttonActive
                  ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  : "bg-muted text-muted-foreground disabled:pointer-events-auto disabled:cursor-default"
              )}
            >
              {isRunning ? (
                <Square
                  className={cn(
                    "size-3 fill-current",
                    buttonActive && "group-hover:animate-icon-pulse"
                  )}
                />
              ) : (
                <ArrowUp
                  className={cn("size-3.5", buttonActive && "group-hover:animate-icon-pulse")}
                />
              )}
            </Button>
          </div>
        </div>
      </div>

      {editorOpen && (
        <SkillEditorModal
          key={editingSkillId ?? "new"}
          open={editorOpen}
          initialData={editingSkillData}
          onClose={() => {
            setEditorOpen(false);
            setEditingSkillId(null);
            setEditingSkillData(undefined);
            setEditorErrors({});
          }}
          onSubmit={handleSaveSkill}
          loading={savingSkill}
          contentLoading={loadingSkillContent}
          errors={editorErrors}
          mode={editorMode}
        />
      )}

      <ConfirmDestructiveDialog
        target={skillPendingDelete}
        title="Delete this skill?"
        description={(skill) => (
          <>&ldquo;{skill.name}&rdquo; will be permanently deleted. This cannot be undone.</>
        )}
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        onConfirm={handleConfirmDeleteSkill}
        onClose={() => setSkillPendingDelete(null)}
      />
    </>
  );
};
